/**
 * "Send it again" — the one control that gets somebody out of every dead end this feature has.
 *
 * It is a component rather than three copies because there are three places a player meets an
 * unconfirmed address and they must not diverge: the check-your-inbox card after registering, a
 * sign-in refused because the address is not confirmed, and a confirmation link that has expired.
 *
 * **IT TAKES THE ADDRESS AS A PROP AND NEVER READS THE SESSION**, because two of those three
 * screens have no session — a login that was refused issues none, and a link is very often
 * opened on a different device from the one that registered. The procedure behind it is public
 * and answers the same thing whatever the address turns out to be, so what this shows on success
 * is deliberately "we have sent one if there is an account here" rather than a claim about who
 * exists.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';

interface ResendVerificationProps {
  /** The address to send to. Empty is allowed: the field below is how it gets filled in. */
  email: string;
  /** Draw an address field when the caller cannot supply one — the expired-link screen. */
  askForEmail?: boolean;
}

export function ResendVerification({ email, askForEmail = false }: ResendVerificationProps) {
  const [typed, setTyped] = useState('');
  const address = askForEmail ? typed : email;
  const resend = trpc.auth.resendVerification.useMutation();

  return (
    <div className="mt-3 space-y-2">
      {askForEmail && (
        <Input
          type="email"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          autoComplete="email"
          placeholder="you@example.com"
          aria-label="Email"
        />
      )}

      <Button
        variant="outline"
        className="w-full"
        disabled={resend.isPending || address.trim() === ''}
        onClick={() => resend.mutate({ email: address })}
      >
        {resend.isPending ? 'Sending…' : 'Send the confirmation email again'}
      </Button>

      {/* The rate limiter's refusal carries a number of seconds, which is the one thing a player
          can actually act on — so it is printed rather than flattened into "try later". */}
      {resend.error && (
        <p className="text-sm text-destructive" role="alert">
          {resend.error.message}
        </p>
      )}

      {resend.isSuccess && !resend.error && (
        <p className="text-sm text-muted-foreground" role="status">
          Sent. If there is an account for that address, the link is on its way — check your spam
          folder too.
        </p>
      )}
    </div>
  );
}
